import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEMANTIC, UI, THEMES, getThemeKey, applyTheme, NAV_STYLES, getNavStyle, setNavStyle } from '../styles/theme';
import API from '../api';
import BackButton from '../components/common/BackButton';
import { tierToDisplayPlan } from '../utils/membershipUtils';

// ── Styles ────────────────────────────────────────────────────────────────────
const cardStyle = {
  backgroundColor: SEMANTIC.backgroundCard,
  borderRadius: UI.borderRadius.md,
  padding: UI.spacing.xl,
  marginBottom: UI.spacing.xl,
  boxShadow: UI.shadow.card,
};

const sectionTitleStyle = {
  color: SEMANTIC.actionBuy,
  fontSize: UI.fontSize.lg,
  fontWeight: UI.fontWeight.bold,
  marginBottom: UI.spacing.lg,
  paddingBottom: UI.spacing.sm,
  borderBottom: `1px solid #333`,
};

const fieldRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${UI.spacing.sm} 0`,
  borderBottom: '1px solid #222',
};

const labelStyle = {
  color: SEMANTIC.textSecondary,
  fontSize: UI.fontSize.sm,
  minWidth: '140px',
};

const valueStyle = {
  color: SEMANTIC.textPrimary,
  fontSize: UI.fontSize.md,
  flex: 1,
  marginLeft: UI.spacing.md,
};

const inputStyle = {
  flex: 1,
  marginLeft: UI.spacing.md,
  padding: `${UI.spacing.sm} ${UI.spacing.md}`,
  backgroundColor: '#333',
  color: SEMANTIC.textPrimary,
  border: `1px solid #555`,
  borderRadius: UI.borderRadius.sm,
  fontSize: UI.fontSize.md,
};

const editBtnStyle = {
  background: 'transparent',
  border: `1px solid ${SEMANTIC.actionBuy}`,
  color: SEMANTIC.actionBuy,
  borderRadius: UI.borderRadius.sm,
  padding: `4px 10px`,
  fontSize: UI.fontSize.sm,
  cursor: 'pointer',
  marginLeft: UI.spacing.sm,
};

const saveBtnStyle = {
  background: SEMANTIC.ctaPrimary,
  border: 'none',
  color: SEMANTIC.ctaPrimaryText,
  borderRadius: UI.borderRadius.sm,
  padding: `4px 12px`,
  fontSize: UI.fontSize.sm,
  fontWeight: UI.fontWeight.bold,
  cursor: 'pointer',
  marginLeft: UI.spacing.sm,
};

const cancelBtnStyle = {
  background: 'transparent',
  border: `1px solid #555`,
  color: SEMANTIC.textSecondary,
  borderRadius: UI.borderRadius.sm,
  padding: `4px 10px`,
  fontSize: UI.fontSize.sm,
  cursor: 'pointer',
  marginLeft: UI.spacing.sm,
};

// ── Component ─────────────────────────────────────────────────────────────────
const Settings = () => {
  const { t, i18n } = useTranslation();
  const userId = localStorage.getItem('userId');

  // Profile (solo per membership)
  const [profile, setProfile] = useState(null);

  // Trading mode state
  const [tradingMode, setTradingMode] = useState('paper');
  const [tradingModeSaving, setTradingModeSaving] = useState(false);
  const [tradingModeMsg, setTradingModeMsg] = useState(null);

  // Alpaca API key state (real trading)
  const [alpacaKey, setAlpacaKey] = useState(null);       // null = not set, string = set
  const [editingApiKey, setEditingApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [secretKeyInput, setSecretKeyInput] = useState('');
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [apiKeyMsg, setApiKeyMsg] = useState(null); // { type: 'ok'|'err', text }

  // Alpaca API key state (paper trading)
  const [alpacaPaperKey, setAlpacaPaperKey] = useState(null);
  const [editingPaperKey, setEditingPaperKey] = useState(false);
  const [paperApiKeyInput, setPaperApiKeyInput] = useState('');
  const [paperSecretKeyInput, setPaperSecretKeyInput] = useState('');
  const [paperKeySaving, setPaperKeySaving] = useState(false);
  const [paperKeyMsg, setPaperKeyMsg] = useState(null);

  // Membership state
  const [membershipSaving, setMembershipSaving] = useState(false);
  const [membershipMsg, setMembershipMsg] = useState(null);
  const [membershipTiers, setMembershipTiers] = useState([]);

  // Autopilot state
  const [autopilot, setAutopilot] = useState(false);
  const [autopilotSaving, setAutopilotSaving] = useState(false);

  // Notification prefs state
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifSaving, setNotifSaving] = useState(false);

  // Theme state
  const [currentTheme, setCurrentTheme] = useState(getThemeKey());
  const [currentNavStyle, setCurrentNavStyle] = useState(getNavStyle());

  // Alpaca section — chiusa di default, solo per esperti
  const [alpacaExpanded, setAlpacaExpanded] = useState(false);

  // ── Fetch profile (solo per trading mode e alpaca key) ────────────────────
  useEffect(() => {
    if (!userId) return;
    API.get(`/users/${userId}/profile`)
      .then(res => {
        setAlpacaKey(res.data.alpaca_api_key || null);
        setAlpacaPaperKey(res.data.alpaca_paper_api_key || null);
        setTradingMode(res.data.trading_mode || 'paper');
        setAutopilot(!!res.data.autopilot);
        setProfile(res.data);
      })
      .catch(() => {});
    API.get('/membership-tiers')
      .then(r => setMembershipTiers((r.data || []).map(tier => tierToDisplayPlan(tier, t))))
      .catch(() => {});
    API.get(`/users/${userId}/notification-prefs`)
      .then(r => setNotifPrefs(r.data))
      .catch(() => {});
  }, [userId, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trading mode handler ───────────────────────────────────────────────────
  const switchTradingMode = async (mode) => {
    if (mode === tradingMode) return;
    if (mode === 'real' && !alpacaKey) {
      setTradingModeMsg({ type: 'err', text: t('settings.trading_mode_needs_key', 'Configura prima la tua API Key Alpaca per abilitare il Real Trading') });
      return;
    }
    setTradingModeSaving(true);
    setTradingModeMsg(null);
    try {
      await API.put(`/users/${userId}/trading-mode`, { trading_mode: mode });
      setTradingMode(mode);
      setTradingModeMsg({ type: 'ok', text: t('settings.trading_mode_saved', { mode: mode === 'real' ? 'Real Trading' : 'Paper Trading' }) });
      setTimeout(() => setTradingModeMsg(null), 3000);
    } catch (err) {
      setTradingModeMsg({ type: 'err', text: err.response?.data?.error || t('settings.trading_mode_error', 'Errore nel salvataggio') });
    } finally {
      setTradingModeSaving(false);
    }
  };

  // ── Autopilot handler ─────────────────────────────────────────────────────
  const toggleAutopilot = async () => {
    setAutopilotSaving(true);
    const newVal = autopilot ? 0 : 1;
    try {
      await API.put(`/users/${userId}/profile`, { autopilot: newVal });
      setAutopilot(!!newVal);
    } catch { /* silent */ }
    finally { setAutopilotSaving(false); }
  };

  // ── Notification prefs handler ────────────────────────────────────────────
  const toggleNotifPref = async (key) => {
    if (!notifPrefs || notifSaving) return;
    const updated = { ...notifPrefs, [key]: notifPrefs[key] ? 0 : 1 };
    setNotifPrefs(updated);
    setNotifSaving(true);
    try {
      await API.put(`/users/${userId}/notification-prefs`, updated);
    } catch { setNotifPrefs(notifPrefs); }
    finally { setNotifSaving(false); }
  };

  // ── Alpaca key handlers ────────────────────────────────────────────────────
  const saveAlpacaKey = async () => {
    if (!apiKeyInput.trim() || !secretKeyInput.trim()) {
      setApiKeyMsg({ type: 'err', text: t('settings.alpaca_key_required', 'Inserisci sia API Key che Secret Key') });
      return;
    }
    setApiKeySaving(true);
    setApiKeyMsg(null);
    try {
      await API.put(`/users/${userId}/alpaca-key`, {
        alpaca_api_key: apiKeyInput.trim(),
        alpaca_secret_key: secretKeyInput.trim(),
      });
      setAlpacaKey(apiKeyInput.trim());
      setApiKeyInput('');
      setSecretKeyInput('');
      setEditingApiKey(false);
      setApiKeyMsg({ type: 'ok', text: t('settings.alpaca_key_saved', 'Chiave Alpaca salvata con successo') });
      setTimeout(() => setApiKeyMsg(null), 3000);
    } catch (err) {
      setApiKeyMsg({ type: 'err', text: err.response?.data?.error || t('settings.alpaca_key_error', 'Errore nel salvataggio') });
    } finally {
      setApiKeySaving(false);
    }
  };

  const deleteAlpacaKey = async () => {
    try {
      await API.delete(`/users/${userId}/alpaca-key`);
      setAlpacaKey(null);
      setEditingApiKey(false);
      setApiKeyMsg({ type: 'ok', text: t('settings.alpaca_key_deleted', 'Chiave Alpaca rimossa') });
      setTimeout(() => setApiKeyMsg(null), 3000);
    } catch (err) {
      setApiKeyMsg({ type: 'err', text: t('settings.alpaca_key_error', 'Errore nella rimozione') });
    }
  };

  // ── Alpaca paper key handlers ──────────────────────────────────────────────
  const savePaperKey = async () => {
    if (!paperApiKeyInput.trim() || !paperSecretKeyInput.trim()) {
      setPaperKeyMsg({ type: 'err', text: t('settings.alpaca_key_required') });
      return;
    }
    setPaperKeySaving(true);
    setPaperKeyMsg(null);
    try {
      await API.put(`/users/${userId}/alpaca-paper-key`, {
        alpaca_paper_api_key: paperApiKeyInput.trim(),
        alpaca_paper_secret_key: paperSecretKeyInput.trim(),
      });
      setAlpacaPaperKey(paperApiKeyInput.trim());
      setPaperApiKeyInput('');
      setPaperSecretKeyInput('');
      setEditingPaperKey(false);
      setPaperKeyMsg({ type: 'ok', text: t('settings.alpaca_paper_key_saved') });
      setTimeout(() => setPaperKeyMsg(null), 3000);
    } catch (err) {
      setPaperKeyMsg({ type: 'err', text: err.response?.data?.error || t('settings.alpaca_key_error') });
    } finally {
      setPaperKeySaving(false);
    }
  };

  const deletePaperKey = async () => {
    try {
      await API.delete(`/users/${userId}/alpaca-paper-key`);
      setAlpacaPaperKey(null);
      setEditingPaperKey(false);
      setPaperKeyMsg({ type: 'ok', text: t('settings.alpaca_paper_key_deleted') });
      setTimeout(() => setPaperKeyMsg(null), 3000);
    } catch {
      setPaperKeyMsg({ type: 'err', text: t('settings.alpaca_key_error') });
    }
  };

  // ── Language ───────────────────────────────────────────────────────────────
  const changeLanguage = async (lang) => {
    await i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    if (userId) {
      try { await API.put(`/users/${userId}/language`, { language: lang }); } catch (e) {}
    }
  };

  const currentLang = i18n.language?.split('-')[0] || 'en';

  const langBtnStyle = (lang) => ({
    padding: `${UI.spacing.sm} ${UI.spacing.lg}`,
    borderRadius: UI.borderRadius.sm,
    border: `2px solid ${currentLang === lang ? SEMANTIC.actionBuy : '#444'}`,
    backgroundColor: currentLang === lang ? 'rgba(0,255,136,0.1)' : 'transparent',
    color: currentLang === lang ? SEMANTIC.actionBuy : SEMANTIC.textSecondary,
    fontWeight: currentLang === lang ? UI.fontWeight.bold : 'normal',
    fontSize: UI.fontSize.md,
    cursor: 'pointer',
    transition: UI.transition.fast,
  });

  // ── Membership handler ─────────────────────────────────────────────────────
  const switchMembership = async (tier) => {
    if (tradingMode === 'paper') return;
    if (tier === (profile?.membership || 'earth')) return;
    setMembershipSaving(true);
    setMembershipMsg(null);
    try {
      await API.put(`/users/${userId}/membership`, { membership: tier });
      setProfile(prev => ({ ...prev, membership: tier }));
      setMembershipMsg({ type: 'ok', text: t('settings.membership_saved') });
      setTimeout(() => setMembershipMsg(null), 4000);
    } catch (err) {
      setMembershipMsg({ type: 'err', text: err.response?.data?.error || t('settings.membership_error') });
    } finally {
      setMembershipSaving(false);
    }
  };

  // ── Theme handler ──────────────────────────────────────────────────────────
  const handleThemeSelect = (key) => {
    setCurrentTheme(key);
    localStorage.setItem('appTheme', key);
    applyTheme(key);
  };

  // ── Nav style handler ───────────────────────────────────────────────────────
  const handleNavStyleSelect = (key) => {
    setCurrentNavStyle(key);
    setNavStyle(key);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ backgroundColor: SEMANTIC.backgroundApp, minHeight: '100vh', padding: UI.spacing.lg }}>
      <BackButton />
      <h2 style={{ color: SEMANTIC.textPrimary, textAlign: 'center', marginBottom: UI.spacing.xl, fontSize: UI.fontSize.title, fontWeight: UI.fontWeight.bold }}>
        {t('settings.title')}
      </h2>

      {/* ── SECTION 1: TRADING MODE ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>{t('settings.section_trading_mode')}</h3>
        <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginBottom: UI.spacing.lg, lineHeight: 1.5 }}>
          {t('settings.trading_mode_desc')}
        </p>

        {tradingModeMsg && (
          <div style={{
            color: tradingModeMsg.type === 'ok' ? SEMANTIC.actionBuy : SEMANTIC.actionSell,
            backgroundColor: tradingModeMsg.type === 'ok' ? 'rgba(0,255,136,0.1)' : 'rgba(255,77,77,0.1)',
            border: `1px solid ${tradingModeMsg.type === 'ok' ? SEMANTIC.actionBuy : SEMANTIC.actionSell}`,
            borderRadius: UI.borderRadius.sm, padding: UI.spacing.md,
            marginBottom: UI.spacing.md, fontSize: UI.fontSize.sm,
          }}>
            {tradingModeMsg.text}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          {[
            { key: 'paper', icon: '🧪', label: 'Paper Trading', descKey: 'trading_mode_paper_desc', color: '#00ff88' },
            { key: 'real',  icon: '🏦', label: 'Real Trading',  descKey: 'trading_mode_real_desc',  color: '#00aaff' },
          ].map(opt => {
            const isActive = tradingMode === opt.key;
            const isDisabled = tradingModeSaving;
            return (
              <div
                key={opt.key}
                onClick={() => !isDisabled && switchTradingMode(opt.key)}
                style={{
                  flex: 1, padding: '14px 16px', borderRadius: UI.borderRadius.md,
                  border: `2px solid ${isActive ? opt.color : '#2a2a40'}`,
                  backgroundColor: isActive ? `${opt.color}0d` : 'transparent',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s',
                  opacity: isDisabled ? 0.6 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 18 }}>{opt.icon}</span>
                  <span style={{ color: isActive ? opt.color : SEMANTIC.textPrimary, fontWeight: 700, fontSize: 14 }}>{opt.label}</span>
                  {isActive && (
                    <span style={{
                      marginLeft: 'auto', fontSize: 10, fontWeight: 700,
                      color: opt.color, backgroundColor: `${opt.color}22`,
                      border: `1px solid ${opt.color}44`,
                      padding: '1px 7px', borderRadius: 20,
                    }}>{t('settings.trading_mode_active')}</span>
                  )}
                </div>
                <p style={{ color: SEMANTIC.textSecondary, fontSize: 12, margin: 0, lineHeight: 1.4 }}>{t(`settings.${opt.descKey}`)}</p>
              </div>
            );
          })}
        </div>

        {tradingMode === 'real' && !alpacaKey && (
          <div style={{
            marginTop: 10, padding: '8px 12px', borderRadius: UI.borderRadius.sm,
            backgroundColor: 'rgba(255,170,0,0.08)', border: '1px solid rgba(255,170,0,0.3)',
            fontSize: 12, color: '#ffaa00',
          }}>
            ⚠ {t('settings.trading_mode_no_key')}
          </div>
        )}
      </div>

      {/* ── SECTION: AUTOPILOT ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>✈️ {t('settings.section_autopilot')}</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: SEMANTIC.textPrimary, fontWeight: 600, fontSize: UI.fontSize.md, marginBottom: 4 }}>
              {t('settings.autopilot_title')}
            </div>
            <div style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, lineHeight: 1.5 }}>
              {t('settings.autopilot_desc')}
            </div>
          </div>
          {/* Toggle switch */}
          <button
            onClick={toggleAutopilot}
            disabled={autopilotSaving}
            style={{
              flexShrink: 0,
              width: 52, height: 28,
              borderRadius: 14,
              border: 'none',
              cursor: autopilotSaving ? 'not-allowed' : 'pointer',
              backgroundColor: autopilot ? SEMANTIC.actionBuy : '#444',
              position: 'relative',
              transition: 'background-color 0.2s',
              padding: 0,
            }}
          >
            <span style={{
              position: 'absolute',
              top: 3, left: autopilot ? 27 : 3,
              width: 22, height: 22,
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
            }} />
          </button>
        </div>
        {autopilot && (
          <div style={{
            marginTop: 12, padding: '8px 12px', borderRadius: UI.borderRadius.sm,
            backgroundColor: 'rgba(0,255,136,0.07)', border: '1px solid rgba(0,255,136,0.25)',
            fontSize: 12, color: SEMANTIC.actionBuy,
          }}>
            {t('settings.autopilot_active_note')}
          </div>
        )}
      </div>

      {/* ── SECTION 2: MEMBERSHIP ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>{t('settings.section_membership')}</h3>
        <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginBottom: UI.spacing.lg, lineHeight: 1.5 }}>
          {t('settings.membership_desc')}
        </p>

        {/* Banner paper trading */}
        {tradingMode === 'paper' && (() => {
          const topTier = membershipTiers[membershipTiers.length - 1];
          if (!topTier) return null;
          return (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              backgroundColor: `${topTier.color}12`,
              border: `1px solid ${topTier.color}44`,
              borderRadius: UI.borderRadius.sm,
              padding: '10px 14px',
              marginBottom: UI.spacing.lg,
              fontSize: UI.fontSize.sm,
            }}>
              <span style={{ fontSize: 20 }}>
                {topTier.img
                  ? <img src={topTier.img} alt={topTier.name} style={{ width: 22, height: 22, borderRadius: '50%' }} />
                  : topTier.icon}
              </span>
              <span style={{ color: topTier.color, fontWeight: 600 }}>
                {t('settings.membership_paper_banner', { tier: topTier.name })}
              </span>
              <span style={{ color: SEMANTIC.textSecondary }}>
                {t('settings.membership_paper_banner_sub')}
              </span>
            </div>
          );
        })()}

        {membershipMsg && (
          <div style={{
            color: membershipMsg.type === 'ok' ? SEMANTIC.actionBuy : SEMANTIC.actionSell,
            backgroundColor: membershipMsg.type === 'ok' ? 'rgba(0,255,136,0.1)' : 'rgba(255,77,77,0.1)',
            border: `1px solid ${membershipMsg.type === 'ok' ? SEMANTIC.actionBuy : SEMANTIC.actionSell}`,
            borderRadius: UI.borderRadius.sm, padding: UI.spacing.md,
            marginBottom: UI.spacing.md, fontSize: UI.fontSize.sm,
          }}>
            {membershipMsg.text}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {membershipTiers.map((tier, idx) => {
            const isPaper = tradingMode === 'paper';
            const topTierKey = membershipTiers[membershipTiers.length - 1]?.key;
            // In paper: mostra Saturn (ultimo tier) come attivo; in real: usa membership reale
            const isActive = isPaper
              ? tier.key === topTierKey
              : (profile?.membership || 'earth') === tier.key;
            // In paper: gli altri tier sono visibili ma non interattivi e leggermente sfumati
            const isDisabled = membershipSaving || isPaper;
            const isDimmed = isPaper && !isActive;
            return (
              <div
                key={tier.key}
                onClick={() => !isDisabled && switchMembership(tier.key)}
                style={{
                  position: 'relative',
                  borderRadius: UI.borderRadius.md,
                  border: `2px solid ${isActive ? tier.color : '#2a2a40'}`,
                  backgroundColor: isActive ? tier.bg : 'rgba(255,255,255,0.02)',
                  padding: '16px 18px',
                  cursor: isDisabled ? 'default' : 'pointer',
                  transition: 'all 0.15s',
                  opacity: isDimmed ? 0.35 : 1,
                  boxShadow: isActive ? `0 0 18px ${tier.color}28` : 'none',
                }}
              >
                {/* Badge PIÙ SCELTO */}
                {tier.popular && !isPaper && (
                  <div style={{
                    position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)',
                    backgroundColor: tier.color, color: '#000',
                    fontSize: 10, fontWeight: 700,
                    padding: '2px 12px', borderRadius: 20, whiteSpace: 'nowrap',
                  }}>
                    {t('settings.membership_popular')}
                  </div>
                )}

                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {tier.img
                      ? <img src={tier.img} alt={tier.name} style={{ width: 36, height: 36, borderRadius: '50%' }} />
                      : <span style={{ fontSize: 30 }}>{tier.icon}</span>}
                    <div>
                      <div style={{ color: isActive ? tier.color : SEMANTIC.textPrimary, fontWeight: 700, fontSize: 16 }}>
                        {tier.name}
                      </div>
                      <div style={{ color: SEMANTIC.textSecondary, fontSize: 11, marginTop: 1 }}>{tier.tagline}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isActive && (
                      <span style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                        color: tier.color, backgroundColor: `${tier.color}22`,
                        border: `1px solid ${tier.color}44`,
                        padding: '2px 9px', borderRadius: 20,
                      }}>
                        {isPaper ? t('settings.membership_paper_badge') : t('settings.membership_active_badge')}
                      </span>
                    )}
                    {!isPaper && (
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%',
                        border: `2px solid ${isActive ? tier.color : '#444'}`,
                        backgroundColor: isActive ? tier.color : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        {isActive && <i className="bi bi-check" style={{ color: '#000', fontSize: 12 }} />}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pricing */}
                <div style={{
                  textAlign: 'center', marginBottom: 12, padding: '8px 12px',
                  backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: UI.borderRadius.sm,
                }}>
                  <div style={{ color: tier.color, fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{tier.pricing.value}</div>
                  <div style={{ color: SEMANTIC.textSecondary, fontSize: 11, marginTop: 3 }}>{tier.pricing.label}</div>
                  {tier.pricing.sub && (
                    <div style={{ color: tier.color, fontSize: 11, marginTop: 2, opacity: 0.85 }}>{tier.pricing.sub}</div>
                  )}
                </div>

                {/* Feature list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {tier.features.map(f => (
                    <div key={f.label} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: 5,
                    }}>
                      <span style={{ color: SEMANTIC.textSecondary, fontSize: 12 }}>{f.label}</span>
                      <span style={{
                        fontSize: 12, fontWeight: 700,
                        color: f.highlight ? tier.color : f.muted ? '#444' : SEMANTIC.textPrimary,
                      }}>{f.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── SECTION ALPACA API KEY ── */}
      <div style={cardStyle}>
        {/* Header sempre visibile — cliccabile per espandere */}
        <div
          onClick={() => setAlpacaExpanded(v => !v)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, marginBottom: alpacaExpanded ? UI.spacing.lg : 0 }}
        >
          <h3 style={{ ...sectionTitleStyle, marginBottom: 0, paddingBottom: 0, borderBottom: 'none', flex: 1 }}>
            {t('settings.section_alpaca', 'Integrazione Alpaca')}
          </h3>
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap',
            backgroundColor: 'rgba(255,170,0,0.12)', color: '#ffaa00',
            border: '1px solid rgba(255,170,0,0.4)', fontWeight: 700,
          }}>
            <i className="bi bi-shield-lock" style={{ marginRight: 4 }} />
            {t('settings.alpaca_expert_badge', 'Solo per esperti')}
          </span>
          <i
            className={`bi bi-chevron-${alpacaExpanded ? 'up' : 'down'}`}
            style={{ color: SEMANTIC.textSecondary, fontSize: 14, flexShrink: 0 }}
          />
        </div>

        {alpacaExpanded && (
          <>
            {/* Banner avviso sezione avanzata */}
            <div style={{
              display: 'flex', gap: 10,
              backgroundColor: 'rgba(255,170,0,0.06)',
              border: '1px solid rgba(255,170,0,0.25)',
              borderRadius: UI.borderRadius.sm,
              padding: `${UI.spacing.sm} ${UI.spacing.md}`,
              marginBottom: UI.spacing.lg,
            }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ color: '#ffaa00', flexShrink: 0, marginTop: 2 }} />
              <p style={{ color: '#ffaa00', fontSize: 11, margin: 0, lineHeight: 1.5 }}>
                {t('settings.alpaca_expert_warning', 'Sezione avanzata. Modifica queste impostazioni solo se hai un account Alpaca e sai cosa stai facendo. Una configurazione errata potrebbe impedire il corretto funzionamento del trading.')}
              </p>
            </div>

            <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginBottom: UI.spacing.lg, lineHeight: 1.5 }}>
              {t('settings.alpaca_desc')}
            </p>

        {/* ── Paper Trading keys ── */}
        <div style={{
          border: '1px solid rgba(0,255,136,0.2)',
          borderRadius: UI.borderRadius.sm,
          padding: UI.spacing.md,
          marginBottom: UI.spacing.lg,
          backgroundColor: 'rgba(0,255,136,0.03)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: UI.spacing.sm }}>
            <span style={{ fontSize: 16 }}>🧪</span>
            <span style={{ color: SEMANTIC.actionBuy, fontWeight: 700, fontSize: UI.fontSize.sm }}>{t('settings.alpaca_paper_title')}</span>
            <span style={{
              marginLeft: 'auto', fontSize: 10, padding: '2px 8px', borderRadius: 20,
              backgroundColor: 'rgba(0,255,136,0.1)', color: SEMANTIC.actionBuy,
              border: '1px solid rgba(0,255,136,0.3)',
            }}>
              {alpacaPaperKey ? t('settings.alpaca_configured') : t('settings.alpaca_not_configured')}
            </span>
          </div>
          <p style={{ color: SEMANTIC.textSecondary, fontSize: 11, margin: `0 0 ${UI.spacing.sm}`, lineHeight: 1.5 }}>
            {t('settings.alpaca_paper_desc')} <code style={{ color: SEMANTIC.actionBuy }}>PK</code>.
          </p>

          {paperKeyMsg && (
            <div style={{
              color: paperKeyMsg.type === 'ok' ? SEMANTIC.actionBuy : SEMANTIC.actionSell,
              backgroundColor: paperKeyMsg.type === 'ok' ? 'rgba(0,255,136,0.1)' : 'rgba(255,77,77,0.1)',
              border: `1px solid ${paperKeyMsg.type === 'ok' ? SEMANTIC.actionBuy : SEMANTIC.actionSell}`,
              borderRadius: UI.borderRadius.sm,
              padding: UI.spacing.sm,
              marginBottom: UI.spacing.sm,
              fontSize: UI.fontSize.sm,
            }}>
              {paperKeyMsg.text}
            </div>
          )}

          <div style={fieldRowStyle}>
            <span style={labelStyle}>API Key</span>
            {alpacaPaperKey ? (
              <span style={{ ...valueStyle, fontFamily: 'monospace', fontSize: 13, color: SEMANTIC.actionBuy }}>
                {alpacaPaperKey.slice(0, 6)}{'•'.repeat(12)}{alpacaPaperKey.slice(-4)}
              </span>
            ) : (
              <span style={{ ...valueStyle, color: '#555' }}>—</span>
            )}
            <button style={editBtnStyle} onClick={() => { setEditingPaperKey(e => !e); setPaperKeyMsg(null); }}>
              <i className={`bi ${editingPaperKey ? 'bi-x' : 'bi-pencil'}`} /> {editingPaperKey ? t('common.cancel') : t('common.edit')}
            </button>
          </div>

          {editingPaperKey && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: UI.spacing.sm, marginTop: UI.spacing.md }}>
              <input
                style={{ ...inputStyle, marginLeft: 0, fontFamily: 'monospace' }}
                type="text"
                placeholder={t('settings.alpaca_paper_key_placeholder')}
                value={paperApiKeyInput}
                onChange={e => setPaperApiKeyInput(e.target.value)}
                autoComplete="off"
              />
              <input
                style={{ ...inputStyle, marginLeft: 0, fontFamily: 'monospace' }}
                type="password"
                placeholder={t('settings.alpaca_paper_secret_placeholder')}
                value={paperSecretKeyInput}
                onChange={e => setPaperSecretKeyInput(e.target.value)}
                autoComplete="off"
              />
              <div style={{ display: 'flex', gap: UI.spacing.sm }}>
                <button style={saveBtnStyle} onClick={savePaperKey} disabled={paperKeySaving}>
                  {paperKeySaving ? t('common.saving') : t('common.save')}
                </button>
                {alpacaPaperKey && (
                  <button onClick={deletePaperKey} style={{ ...cancelBtnStyle, color: SEMANTIC.actionSell, borderColor: SEMANTIC.actionSell }}>
                    {t('settings.alpaca_remove')}
                  </button>
                )}
                <button style={cancelBtnStyle} onClick={() => { setEditingPaperKey(false); setPaperApiKeyInput(''); setPaperSecretKeyInput(''); }}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Real Trading keys ── */}
        <div style={{
          border: '1px solid rgba(0,170,255,0.2)',
          borderRadius: UI.borderRadius.sm,
          padding: UI.spacing.md,
          backgroundColor: 'rgba(0,170,255,0.03)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: UI.spacing.sm }}>
            <span style={{ fontSize: 16 }}>🏦</span>
            <span style={{ color: '#00aaff', fontWeight: 700, fontSize: UI.fontSize.sm }}>{t('settings.alpaca_real_title')}</span>
            <span style={{
              marginLeft: 'auto', fontSize: 10, padding: '2px 8px', borderRadius: 20,
              backgroundColor: 'rgba(0,170,255,0.1)', color: '#00aaff',
              border: '1px solid rgba(0,170,255,0.3)',
            }}>
              {alpacaKey ? t('settings.alpaca_configured') : t('settings.alpaca_not_configured')}
            </span>
          </div>
          <p style={{ color: SEMANTIC.textSecondary, fontSize: 11, margin: `0 0 ${UI.spacing.sm}`, lineHeight: 1.5 }}>
            {t('settings.alpaca_real_desc')} <code style={{ color: '#00aaff' }}>AK</code>.
          </p>

          {apiKeyMsg && (
            <div style={{
              color: apiKeyMsg.type === 'ok' ? SEMANTIC.actionBuy : SEMANTIC.actionSell,
              backgroundColor: apiKeyMsg.type === 'ok' ? 'rgba(0,255,136,0.1)' : 'rgba(255,77,77,0.1)',
              border: `1px solid ${apiKeyMsg.type === 'ok' ? SEMANTIC.actionBuy : SEMANTIC.actionSell}`,
              borderRadius: UI.borderRadius.sm,
              padding: UI.spacing.sm,
              marginBottom: UI.spacing.sm,
              fontSize: UI.fontSize.sm,
            }}>
              {apiKeyMsg.text}
            </div>
          )}

          <div style={fieldRowStyle}>
            <span style={labelStyle}>{t('settings.alpaca_api_key', 'API Key')}</span>
            {alpacaKey ? (
              <span style={{ ...valueStyle, fontFamily: 'monospace', fontSize: 13, color: '#00aaff' }}>
                {alpacaKey.slice(0, 6)}{'•'.repeat(12)}{alpacaKey.slice(-4)}
              </span>
            ) : (
              <span style={{ ...valueStyle, color: '#555' }}>{t('settings.not_set', '—')}</span>
            )}
            <button style={editBtnStyle} onClick={() => { setEditingApiKey(e => !e); setApiKeyMsg(null); }}>
              <i className={`bi ${editingApiKey ? 'bi-x' : 'bi-pencil'}`} /> {editingApiKey ? t('common.cancel') : t('common.edit')}
            </button>
          </div>

          {editingApiKey && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: UI.spacing.sm, marginTop: UI.spacing.md }}>
              <input
                style={{ ...inputStyle, marginLeft: 0, fontFamily: 'monospace' }}
                type="text"
                placeholder={t('settings.alpaca_key_placeholder', 'API Key (es. AKxxxxxxxxxxxxxxx)')}
                value={apiKeyInput}
                onChange={e => setApiKeyInput(e.target.value)}
                autoComplete="off"
              />
              <input
                style={{ ...inputStyle, marginLeft: 0, fontFamily: 'monospace' }}
                type="password"
                placeholder={t('settings.alpaca_secret_placeholder', 'Secret Key')}
                value={secretKeyInput}
                onChange={e => setSecretKeyInput(e.target.value)}
                autoComplete="off"
              />
              <div style={{ display: 'flex', gap: UI.spacing.sm }}>
                <button style={saveBtnStyle} onClick={saveAlpacaKey} disabled={apiKeySaving}>
                  {apiKeySaving ? t('common.saving', 'Salvataggio...') : t('common.save', 'Salva')}
                </button>
                {alpacaKey && (
                  <button
                    onClick={deleteAlpacaKey}
                    style={{ ...cancelBtnStyle, color: SEMANTIC.actionSell, borderColor: SEMANTIC.actionSell }}
                  >
                    {t('settings.alpaca_remove', 'Rimuovi chiave')}
                  </button>
                )}
                <button style={cancelBtnStyle} onClick={() => { setEditingApiKey(false); setApiKeyInput(''); setSecretKeyInput(''); }}>
                  {t('common.cancel', 'Annulla')}
                </button>
              </div>
            </div>
          )}
        </div>
          </>
        )}
      </div>

      {/* ── SECTION 4: LINGUA + TEMA + NAVIGAZIONE ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>{t('settings.section_display')}</h3>

        {/* Lingua */}
        <div style={{ marginBottom: UI.spacing.xl }}>
          <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginBottom: UI.spacing.md }}>{t('settings.section_language')}</p>
          <div style={{ display: 'flex', gap: UI.spacing.md }}>
            <button style={langBtnStyle('en')} onClick={() => changeLanguage('en')}>🇬🇧 English</button>
            <button style={langBtnStyle('it')} onClick={() => changeLanguage('it')}>🇮🇹 Italiano</button>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #333', margin: `0 0 ${UI.spacing.xl}` }} />

        {/* Tema app */}
        <div style={{ marginBottom: UI.spacing.xl }}>
          <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginBottom: UI.spacing.md }}>🎨 {t('settings.section_theme')}</p>
          <div style={{ display: 'flex', gap: UI.spacing.lg, flexWrap: 'wrap' }}>
            {Object.entries(THEMES).map(([key, theme]) => {
              const isActive = currentTheme === key;
              return (
                <button
                  key={key}
                  onClick={() => handleThemeSelect(key)}
                  style={{
                    background: theme.backgroundApp,
                    border: `2px solid ${isActive ? theme.actionBuy : '#444'}`,
                    borderRadius: UI.borderRadius.md,
                    padding: UI.spacing.lg,
                    cursor: 'pointer',
                    minWidth: '110px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: UI.spacing.sm,
                    boxShadow: isActive ? `0 0 12px ${theme.actionBuy}55` : 'none',
                    transition: UI.transition.fast,
                    outline: 'none',
                  }}
                >
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {theme.previewDots.map((color, i) => (
                      <div key={i} style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: color, border: '1px solid #555' }} />
                    ))}
                  </div>
                  <span style={{ color: theme.textPrimary, fontSize: UI.fontSize.sm, fontWeight: UI.fontWeight.bold, marginTop: '2px' }}>
                    {theme.name}
                  </span>
                  {isActive && (
                    <span style={{ color: theme.actionBuy, fontSize: UI.fontSize.xs }}>{t('settings.active_badge')}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ borderTop: '1px solid #333', margin: `0 0 ${UI.spacing.xl}` }} />

        {/* Stile navigazione */}
        <div>
          <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginBottom: UI.spacing.md }}>🧭 {t('settings.section_nav')}</p>
          <div style={{ display: 'flex', gap: UI.spacing.lg, flexWrap: 'wrap' }}>
            {Object.entries(NAV_STYLES).map(([key, style]) => {
              const isActive = currentNavStyle === key;
              return (
                <button
                  key={key}
                  onClick={() => handleNavStyleSelect(key)}
                  style={{
                    background: SEMANTIC.backgroundApp,
                    border: `2px solid ${isActive ? SEMANTIC.ctaPrimary : '#444'}`,
                    borderRadius: UI.borderRadius.md,
                    padding: UI.spacing.lg,
                    cursor: 'pointer',
                    minWidth: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: UI.spacing.sm,
                    boxShadow: isActive ? `0 0 12px ${SEMANTIC.ctaPrimary}55` : 'none',
                    transition: UI.transition.fast,
                    outline: 'none',
                  }}
                >
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', padding: '6px 10px' }}>
                    {key === 'text' && (
                      <>
                        <span style={{ fontSize: '9px', color: SEMANTIC.ctaPrimary, border: `1px solid ${SEMANTIC.ctaPrimary}`, borderRadius: '10px', padding: '2px 6px' }}>Perf</span>
                        <span style={{ fontSize: '9px', color: SEMANTIC.ctaPrimaryText, backgroundColor: SEMANTIC.ctaPrimary, borderRadius: '10px', padding: '2px 6px', fontWeight: 'bold' }}>Pred</span>
                        <span style={{ fontSize: '9px', color: SEMANTIC.ctaPrimary, border: `1px solid ${SEMANTIC.ctaPrimary}`, borderRadius: '10px', padding: '2px 6px' }}>Trade</span>
                      </>
                    )}
                    {key === 'icon' && (
                      <>
                        <i className="bi bi-bar-chart-line" style={{ fontSize: '16px', color: SEMANTIC.ctaPrimary }} />
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: `2px solid ${SEMANTIC.ctaPrimary}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '10px', fontWeight: 'bold', color: SEMANTIC.ctaPrimary }}>K</span>
                        </div>
                        <i className="bi bi-arrow-left-right" style={{ fontSize: '16px', color: SEMANTIC.ctaPrimary }} />
                      </>
                    )}
                    {key === 'icon-text' && (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <i className="bi bi-bar-chart-line" style={{ fontSize: '13px', color: SEMANTIC.ctaPrimary }} />
                          <span style={{ fontSize: '7px', color: SEMANTIC.ctaPrimary }}>Perf</span>
                        </div>
                        <div style={{ width: '28px', height: '28px', borderRadius: '50%', border: `2px solid ${SEMANTIC.ctaPrimary}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: '10px', fontWeight: 'bold', color: SEMANTIC.ctaPrimary }}>K</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <i className="bi bi-arrow-left-right" style={{ fontSize: '13px', color: SEMANTIC.ctaPrimary }} />
                          <span style={{ fontSize: '7px', color: SEMANTIC.ctaPrimary }}>Trade</span>
                        </div>
                      </>
                    )}
                  </div>
                  <span style={{ color: SEMANTIC.textPrimary, fontSize: UI.fontSize.sm, fontWeight: UI.fontWeight.bold }}>
                    {style.label}
                  </span>
                  {isActive && (
                    <span style={{ color: SEMANTIC.ctaPrimary, fontSize: UI.fontSize.xs }}>{t('settings.active_badge')}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── SECTION: NOTIFICHE ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>🔔 {t('settings.section_notifications')}</h3>
        <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginBottom: UI.spacing.lg }}>
          {t('settings.notifications_desc')}
        </p>

        {/* Header colonne */}
        <div style={{ display: 'flex', alignItems: 'center', padding: `0 0 ${UI.spacing.sm}`, borderBottom: '1px solid #333', marginBottom: UI.spacing.sm }}>
          <div style={{ flex: 1, color: SEMANTIC.textSecondary, fontSize: UI.fontSize.xs, textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('settings.notif_event')}
          </div>
          <div style={{ width: 80, textAlign: 'center', color: SEMANTIC.textSecondary, fontSize: UI.fontSize.xs, textTransform: 'uppercase', letterSpacing: 1 }}>
            {t('settings.notif_channel_email')}
          </div>
          <div style={{ width: 80, textAlign: 'center', color: '#444', fontSize: UI.fontSize.xs, textTransform: 'uppercase', letterSpacing: 1 }}>
            SMS
          </div>
        </div>

        {[
          { key: 'order_executed',    label: t('settings.notif_event_order_executed') },
          { key: 'portfolio_created', label: t('settings.notif_event_portfolio_created') },
          { key: 'portfolio_closed',  label: t('settings.notif_event_portfolio_closed') },
          { key: 'fund_withdraw',     label: t('settings.notif_event_fund_withdraw') },
        ].map(({ key, label }) => {
          const emailKey = `email_${key}`;
          const emailOn  = notifPrefs ? !!notifPrefs[emailKey] : true;
          const Toggle = ({ on, onClick, disabled }) => (
            <button
              onClick={onClick}
              disabled={disabled}
              style={{
                width: 44, height: 24, borderRadius: 12, border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                backgroundColor: on ? SEMANTIC.actionBuy : '#333',
                position: 'relative', transition: 'background-color 0.2s', padding: 0,
                opacity: disabled ? 0.4 : 1,
              }}
            >
              <span style={{
                position: 'absolute', top: 2, left: on ? 22 : 2,
                width: 20, height: 20, borderRadius: '50%', backgroundColor: '#fff',
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
              }} />
            </button>
          );
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', padding: `${UI.spacing.sm} 0`, borderBottom: '1px solid #1a1a1a' }}>
              <div style={{ flex: 1, color: SEMANTIC.textPrimary, fontSize: UI.fontSize.sm }}>{label}</div>
              <div style={{ width: 80, display: 'flex', justifyContent: 'center' }}>
                <Toggle on={emailOn} onClick={() => toggleNotifPref(emailKey)} disabled={!notifPrefs || notifSaving} />
              </div>
              <div style={{ width: 80, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Toggle on={false} onClick={() => {}} disabled={true} />
                <span style={{ fontSize: 9, color: '#444' }}>{t('settings.notif_coming_soon')}</span>
              </div>
            </div>
          );
        })}

        {/* Report settimanale Mr. K */}
        {(() => {
          const on = notifPrefs ? !!notifPrefs['email_weekly_report'] : true;
          const Toggle = ({ on: isOn, onClick, disabled }) => (
            <button
              onClick={onClick}
              disabled={disabled}
              style={{
                width: 44, height: 24, borderRadius: 12, border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                backgroundColor: isOn ? SEMANTIC.actionBuy : '#333',
                position: 'relative', transition: 'background-color 0.2s', padding: 0,
                opacity: disabled ? 0.4 : 1,
              }}
            >
              <span style={{
                position: 'absolute', top: 2, left: isOn ? 22 : 2,
                width: 20, height: 20, borderRadius: '50%', backgroundColor: '#fff',
                transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
              }} />
            </button>
          );
          return (
            <div style={{ display: 'flex', alignItems: 'center', padding: `${UI.spacing.sm} 0`, borderBottom: '1px solid #1a1a1a' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: SEMANTIC.textPrimary, fontSize: UI.fontSize.sm }}>
                  {t('settings.notif_event_weekly_report')}
                </div>
                <div style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.xs, marginTop: 2 }}>
                  {t('settings.notif_weekly_report_desc')}
                </div>
              </div>
              <div style={{ width: 80, display: 'flex', justifyContent: 'center' }}>
                <Toggle on={on} onClick={() => toggleNotifPref('email_weekly_report')} disabled={!notifPrefs || notifSaving} />
              </div>
              <div style={{ width: 80 }} />
            </div>
          );
        })()}
      </div>

      {/* ── VERSION FOOTER ── */}
      {process.env.REACT_APP_BUILD_DATE && (
        <div style={{ textAlign: 'center', padding: `${UI.spacing.lg} 0 ${UI.spacing.sm}`, color: '#333', fontSize: UI.fontSize.xs }}>
          {t('settings.version_info', 'Versione del {{date}}', { date: process.env.REACT_APP_BUILD_DATE })}
        </div>
      )}
    </div>
  );
};

export default Settings;
